import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useStaticQuery, graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Button } from "../../components";
import { GenseqLogo, AbbvieLogo, RcsiLogo } from "../../assets/svgs";
import { HeaderPattern } from "../../assets/animated-svgs";
import { scrollToAnchor } from "../../utils";
import { useWindowSize } from "../../hooks";

const Header = () => {
  const { windowWidth } = useWindowSize();
  const containerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const { backgroundImage, kynosLogo } = useStaticQuery(
    graphql`
      query {
        backgroundImage: file(
          relativePath: { eq: "assets/images/header-background.png" }
        ) {
          id
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, quality: 100)
          }
        }
        kynosLogo: file(relativePath: { eq: "assets/images/kynos-logo.png" }) {
          id
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, width: 106, quality: 100)
          }
        }
      }
    `
  );

  useEffect(() => {
    // Small delay to ensure smooth animation start
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Container id="top" ref={containerRef}>
      {windowWidth >= 1024 && (
        <GreenGradient
          as={motion.div}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={
            isLoaded ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }
          }
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
      )}
      <OrangeGradient
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={isLoaded ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      />
      <Pattern
        as={motion.div}
        initial={{ opacity: 0, x: 50 }}
        animate={isLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
      >
        <HeaderPattern containerRef={containerRef} />
      </Pattern>
      <Background
        as={motion.div}
        initial={{ opacity: 0, scale: 1.05 }}
        animate={
          isLoaded ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 1.05 }
        }
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <GatsbyImage
          image={getImage(backgroundImage)}
          alt="Background gradient"
          style={{ zIndex: 0, height: "100%" }}
        />
      </Background>
      <Content
        as={motion.div}
        initial={{ opacity: 0, y: 30 }}
        animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
      >
        {windowWidth >= 1024 && (
          <LogoContainer
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={isLoaded ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.6 }}
          >
            <GenseqLogo width={292} />
          </LogoContainer>
        )}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }}
        >
          Advancing the impact of genetics to improve patient lives
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 1, ease: "easeOut" }}
        >
          Genseq provides ISO 15189 accredited clinical genetic testing services
          to healthcare professionals and a comprehensive range of genomic
          solutions to the biopharma and clinical research sectors worldwide.
          <Button
            text="Our Services"
            id="our-services-header"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              scrollToAnchor("services", {
                duration: 1200,
                offset: -120,
              });
            }}
          />
        </motion.p>
      </Content>
    </Container>
  );
};

const Container = styled.header`
  background: var(--genseq-blue);
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  transition: opacity 0.3s ease;
  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(12, 1fr);
  }
`;

const Background = styled.div`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;
  overflow: hidden;
`;

const GreenGradient = styled.div`
  position: absolute;
  background: linear-gradient(
    280deg,
    rgba(26, 226, 153, 1) 0%,
    rgba(26, 226, 153, 0) 50%
  );
  mix-blend-mode: hard-light;
  width: 600px;
  height: 150%;
  top: -20%;
  right: 0;
  z-index: 4;

  transform-origin: center center;
  animation: moveVertical 5s ease infinite alternate;
  animation-delay: 1s;
  opacity: 1;
  z-index: 3;

  @keyframes moveVertical {
    0% {
      transform: translate(25%, -20%);
    }
    50% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(10%, -20%);
    }
  }
`;

const Pattern = styled.div`
  position: absolute;
  top: 52%;
  right: -30%;
  height: 80%;
  width: 100%;
  z-index: 2;
  transform: translate(0, -50%);
  svg {
    height: 100%;
    width: auto;
    right: 0;
  }
  @media screen and (min-width: 1024px) {
    top: 50%;
    right: -55%;
    svg {
      width: 100%;
    }
  }
`;

const OrangeGradient = styled.div`
  position: absolute;
  background: linear-gradient(
    -280deg,
    rgba(255, 125, 80, 1) 0%,
    rgba(255, 125, 80, 0) 40%
  );
  mix-blend-mode: hard-light;
  width: 400px;
  height: 100%;
  bottom: 0;
  left: 0;
  z-index: 3;

  transform: translate(-140px, 0);
  opacity: 1;
  z-index: 3;
`;

const Content = styled.div`
  display: flex;
  color: white;
  flex-direction: column;
  gap: 1.25rem;
  grid-column: 1 / 7;
  padding: 0 1.25rem;
  margin: 12rem 0 10rem 0;
  position: relative;
  z-index: 4;
  justify-content: flex-end;
  h1 {
    font-size: 2.875rem;
    font-weight: 400;
    max-width: 925px;
  }
  p {
    display: flex;
    flex-direction: column;
    font-size: 1.25rem;
    font-weight: 300;
    gap: 2rem;
    max-width: 690px;
  }
  @media screen and (min-width: 1024px) {
    justify-content: center;
    gap: 3.25rem;
    margin: 6rem 0 9rem 0;
    padding: unset;
    grid-column: 2 / 12;
    h1 {
      font-size: 4rem;
    }
  }
  @media screen and (min-width: 1600px) {
    grid-column: 3 / 11;
  }
`;

const LogoContainer = styled.div``;

const Partners = styled.div`
  display: flex;
  flex-direction: column;
  h5 {
    font-size: 1rem;
    font-weight: 300;
  }
  > div {
    margin-left: -0.5rem;
    align-items: center;
    display: flex;
    gap: 1rem;
  }
`;

export default Header;
